/**
 * IDI-540 (IDI-194 slice B): turn IDI-539 reader rows into display rows.
 * Pure — no I/O, no React. Reader rows arrive oldest first and keep that order.
 */

const EVENT_LABELS = {
  sent: 'Sent',
  resent: 'Resent',
  accepted: 'Accepted',
  decision: 'Accepted',
  declined: 'Declined',
  reopened: 'Reopened',
  expired: 'Expired',
  // Migrated quote_acknowledgement rows (IDI-539 expandLegacyAck).
  acknowledged: 'Accepted',
  rejected: 'Declined',
};

const SEND_TYPES = new Set(['sent', 'resent']);
const ACCEPT_TYPES = new Set(['accepted', 'decision', 'acknowledged']);

// IDI-541: an accept row with no AcceptedConfiguration has nothing to render.
export const DOWNLOAD_NO_SNAPSHOT_REASON = 'No accepted configuration on file';
export const DOWNLOAD_LEGACY_REASON = 'Migrated record — no signed copy on file';
export const MIGRATED_OPEN_REASON = 'Migrated record — original document not available';
// IDI-568 (D2): the latest send, followed by a Reopen with no re-send, is no
// longer the current document.
const REOPENED_AFTER_SEND_REASON = 'Reopened after this send';
// IDI-581 (D1): on a job's tab the converted quote's live document can't be
// opened — ConvertedQuoteGuard (IDI-157) sends /quotes/:id/review to the job.
const CONVERTED_TO_JOB_REASON = 'Converted to this job';

// IDI-556: a customer's decline of a change order reads "Rejected".
const CO_EVENT_LABELS = { declined: 'Rejected', rejected: 'Rejected' };

export function eventLabel(type, documentType) {
  if (documentType === 'change_order' && CO_EVENT_LABELS[type]) return CO_EVENT_LABELS[type];
  return EVENT_LABELS[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');
}

export function howLabel(how) {
  if (how === 'esign') return 'E-sign';
  if (how === 'manual') return 'Manual';
  return '';
}

// IDI-542: rows of a job tab mix the quote's and its change orders'; send
// numbers, the latest send and Reopen are counted per document.
function docKey(r) {
  return `${r?.documentType || ''}:${r?.documentId || ''}`;
}

/**
 * @param {Array<object>} rows reader rows
 * @param {{ context?: string }} [options] IDI-581: context 'job' when the rows
 *   are shown on a job's tab (the quote there is converted).
 * @returns {Array<object>} display rows: the reader row plus
 *   { key, eventLabel, howLabel, sendLabel, superseded, migrated,
 *     openable, openTarget, openReason, showDownload, downloadable,
 *     downloadConfigId, downloadReason }
 */
export function deriveAcknowledgmentRows(rows, { context } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  // Send numbers, in reader order, per document.
  const latestSendIdx = {};
  list.forEach((r, i) => { if (SEND_TYPES.has(r?.type)) latestSendIdx[docKey(r)] = i; });
  // IDI-568: a 'reopened' row after the latest send voids it as the live document.
  const reopenedAfterLatestSend = {};
  list.forEach((r, i) => {
    const k = docKey(r);
    if (r?.type === 'reopened' && latestSendIdx[k] !== undefined && i > latestSendIdx[k]) {
      reopenedAfterLatestSend[k] = true;
    }
  });

  const sendNos = {};
  return list.map((r, i) => {
    const type = r?.type;
    const k = docKey(r);
    const isSend = SEND_TYPES.has(type);
    if (isSend) sendNos[k] = (sendNos[k] || 0) + 1;
    const sendNo = sendNos[k] || 0;
    const isLatestSend = isSend && i === latestSendIdx[k];
    const isCO = r?.documentType === 'change_order';
    const sendLabel = sendNo > 0 ? `Send ${sendNo}` : '';
    // IDI-561: only legacy (migrated) rows are "Migrated record". Derived
    // rows rebuild a native quote's pre-event-log history and carry no label.
    const migrated = r?.source === 'legacy';
    const superseded = isSend && !isLatestSend;
    const isAccept = ACCEPT_TYPES.has(type);
    // IDI-541: the signed PDF renders from this row's own AcceptedConfiguration.
    // Migrated (legacy) rows keep no Download (B-18). IDI-770: a change
    // order's prints from its acceptance's stored record, as a quote's.
    const downloadable = isAccept && r?.source !== 'legacy' && !!r?.acceptedConfigurationId;
    let downloadReason = '';
    if (isAccept && !downloadable) {
      if (r?.source === 'legacy') downloadReason = DOWNLOAD_LEGACY_REASON;
      else downloadReason = DOWNLOAD_NO_SNAPSHOT_REASON;
    }

    let openable = false;
    let openTarget = null;
    let openReason = '';
    if (migrated) {
      openReason = MIGRATED_OPEN_REASON;
    } else if (superseded) {
      openReason = `Superseded by Send ${sendNo + 1}`;
    } else if (isAccept && r?.acceptedConfigurationId) {
      openable = true;
      openTarget = { kind: 'acceptedConfiguration', id: r.acceptedConfigurationId };
    } else if (isLatestSend && reopenedAfterLatestSend[k]) {
      openReason = REOPENED_AFTER_SEND_REASON;
    } else if (isLatestSend && !r?.migratedChangeOrder) {
      // IDI-581: earlier reasons (D2) win; CO rows still open (D3).
      if (context === 'job' && !isCO) {
        openReason = CONVERTED_TO_JOB_REASON;
      } else {
        openable = true;
        openTarget = { kind: 'liveDocument' };
      }
    }

    return {
      ...r,
      key: r?.id ? `${r.id}:${type}:${i}` : `${type}:${r?.at}:${i}`,
      eventLabel: eventLabel(type, r?.documentType),
      howLabel: howLabel(r?.how),
      sendLabel,
      superseded,
      migrated,
      openable,
      openTarget,
      openReason,
      showDownload: isAccept,
      downloadable,
      downloadConfigId: downloadable ? r.acceptedConfigurationId : '',
      downloadReason,
    };
  });
}
